import React from 'react'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { PDFViewer } from '@react-pdf/renderer'

import { QRCodeDocument } from '@/components/organisms/QrCode'
import type { ModalKey, ModalPayloads, ModalProps } from './modal.type'

type ModalConfig<TPayload> = {
  // The provider will inject hideModal into payload before calling render
  render: (payload: TPayload & { hideModal: () => void }) => React.ReactNode
  style?: 'wide' | 'info' | 'form'
}

type ModalConfigMap = {
  [K in ModalKey]: ModalConfig<ModalPayloads[K]>
}

const ModalSkeleton = () => (
  <div className="animate-pulse p-4">
    <div className="mb-3 h-5 w-1/3 rounded bg-gray-200" />
    <div className="space-y-3">
      {[0, 1, 2].map((key) => (
        <div key={key} className="h-10 rounded bg-gray-100" />
      ))}
    </div>
  </div>
)

function createLazyModal<TProps>(
  loader: () => Promise<{ default: React.ComponentType<TProps> }>,
) {
  const Lazy = dynamic(loader, {
    ssr: false,
    loading: () => <ModalSkeleton />,
  })
  return { Lazy }
}

const ProductStatusModal = createLazyModal(() =>
  import('@/components/modals/ProductStatusModal/productStatus.modal').then(
    (m) => ({
      default: m.ProductStatusModal,
    }),
  ),
)

const ProductRequestModal = createLazyModal(() =>
  import('@/components/modals/ProductRequestModal/productRequest.modal').then(
    (m) => ({ default: m.ProductRequestModal }),
  ),
)

const CatalogPreview = createLazyModal(() =>
  import('@/components/organisms/CatalogPreview').then((m) => ({
    default: m.CatalogPreview,
  })),
)

const DeleteProductsModal = createLazyModal(() =>
  import('@/components/modals/DeleteProducts/deleteProducts.modal').then(
    (m) => ({ default: m.DeleteProductsModal }),
  ),
)

const EditPlaceModal = createLazyModal(() =>
  import('@/components/modals/EditPlaceModal/editPlace.modal').then((m) => ({
    default: m.EditPlaceModal,
  })),
)

const DuplicateProjectModal = createLazyModal(() =>
  import('@/components/modals/DuplicateProject/duplicateProject.modal').then(
    (m) => ({
      default: m.DuplicateProjectModal,
    }),
  ),
)

const ConfirmationCard = createLazyModal(() =>
  import('@/components/atoms/ConfirmationCard/confirmationCard.atom').then(
    (m) => ({ default: m.ConfirmationCard }),
  ),
)

const EditTeamMemberModal = createLazyModal(() =>
  import('@/components/modals/EditTeamMemberModal/editTeamMember.modal').then(
    (m) => ({ default: m.EditTeamMemberModal }),
  ),
)

const DocumentUploadModal = createLazyModal(() =>
  import('@/components/modals/DocumentUploadModal/documentUpload.modal').then(
    (m) => ({ default: m.DocumentUploadModal }),
  ),
)

const ServiceUploadModal = createLazyModal(() =>
  import('@/components/modals/ServiceUpload/serviceUpload.modal').then((m) => ({
    default: m.ServiceUploadModal,
  })),
)

const CustomerUploadModal = createLazyModal(() =>
  import('@/components/modals/CustomerUpload.modal.tsx/customerUpload.modal').then(
    (m) => ({ default: m.CustomerUploadModal }),
  ),
)

const UserSurveyModal = createLazyModal(() =>
  import('@/components/modals/UserSurvey/userSurvey.modal').then((m) => ({
    default: m.UserSurveyModal,
  })),
)

const ModalConfirmationCode = createLazyModal(() =>
  import('@/components/modals/ModalConfirmationCode/modalConfirmationCode.modal').then(
    (m) => ({ default: m.ModalConfirmationCode }),
  ),
)

const ForgotPasswordModal = createLazyModal<ModalProps<'forgotPassword'>>(() =>
  import('@/components/modals/ForgotPassword/forgotPassword.modal').then(
    (m) => ({ default: m.ForgotPasswordModal }),
  ),
)

const NewPasswordRequiredModal = createLazyModal(() =>
  import('@/components/modals/NewPasswordRequired/newPasswordRequired.modal').then(
    (m) => ({ default: m.NewPasswordRequiredModal }),
  ),
)

const UpdatePasswordFormModal = createLazyModal(() =>
  import('@/components/modals/UpdatePasswordForm/updatePassword.modal').then(
    (m) => ({ default: m.UpdatePasswordFormModal }),
  ),
)

const DeleteAccountConfirmation = createLazyModal(() =>
  import('@/components/modals/DeleteAccount/deleteAccountModal.modal').then(
    (m) => ({ default: m.DeleteAccountConfirmation }),
  ),
)

const DeleteListModal = createLazyModal(() =>
  import('@/components/modals/DeleteList/index').then((m) => ({
    default: m.DeleteListModal,
  })),
)

const EditProperty = createLazyModal(() =>
  import('@/components/modals/EditProperty/index').then((m) => ({
    default: m.EditProperty,
  })),
)

const EditAlert = createLazyModal(() =>
  import('@/components/modals/EditAlert/index').then((m) => ({
    default: m.EditAlert,
  })),
)

const ProductDetailsModal = createLazyModal(() =>
  import('@/components/modals/ProductDetailsModal/productsDetails.modal').then(
    (m) => ({ default: m.ProductDetailsModal }),
  ),
)

const RestoreProductModal = createLazyModal(() =>
  import('@/components/modals/RestoreProducts/restoreProducts.modal').then(
    (m) => ({ default: m.RestoreProductModal }),
  ),
)

const TasksModal = createLazyModal(() =>
  import('@/components/modals/TasksModal/tasks.modal').then((m) => ({
    default: m.TasksModal,
  })),
)

const EditOrganizationModal = createLazyModal(() =>
  import('@/components/modals/EditOrganizationModal/editOrganization.modal').then(
    (m) => ({ default: m.EditOrganizationModal }),
  ),
)

const AlertResults = createLazyModal(() =>
  import('@/components/modals/AlertResults/index').then((m) => ({
    default: m.AlertResults,
  })),
)

const CatalogExportOptionsModal = createLazyModal(() =>
  import('@/components/modals/CatalogExportOptionsModal/catalogExportOptions.modal').then(
    (m) => ({ default: m.OptionsModal }),
  ),
)

const ProductEditModal = createLazyModal(() =>
  import('@/components/organisms/CatalogEditor/modals/ProductEditModal').then(
    (m) => ({ default: m.ProductEditModal }),
  ),
)

const EditMember = createLazyModal(() =>
  import('@/components/organisms/EditMember').then((m) => ({
    default: m.EditMember,
  })),
)

const EditReference = createLazyModal(() =>
  import('@/components/organisms/EditReference').then((m) => ({
    default: m.EditReference,
  })),
)

const OptionsImport = createLazyModal(() =>
  import('@/components/organisms/OptionsImport').then((m) => ({
    default: m.OptionsImport,
  })),
)

const AddSectionModal = createLazyModal(() =>
  import('@/components/organisms/Blueprint/AddSectionModal').then((m) => ({
    default: m.AddSectionModal,
  })),
)

const EditSectionModal = createLazyModal(() =>
  import('@/components/organisms/Blueprint/EditSectionModal').then((m) => ({
    default: m.EditSectionModal,
  })),
)

const MoveSectionModal = createLazyModal(() =>
  import('@/components/organisms/Blueprint/MoveSectionModal').then((m) => ({
    default: m.MoveSectionModal,
  })),
)

const EditMarkerModal = createLazyModal(() =>
  import('@/components/organisms/Blueprint/EditMarkerModal').then((m) => ({
    default: m.EditMarkerModal,
  })),
)

const EditZoneModal = createLazyModal(() =>
  import('@/components/organisms/Blueprint/EditZoneModal').then((m) => ({
    default: m.default,
  })),
)

const ImportQuantityModal = createLazyModal(() =>
  import('@/components/organisms/Blueprint/ImportQuantityModal').then((m) => ({
    default: m.ImportQuantityModal,
  })),
)

const BlueprintConfirmationModal = createLazyModal(() =>
  import('@/components/organisms/Blueprint/ConfirmationModal').then((m) => ({
    default: m.default,
  })),
)

const QuoteCreationModal = createLazyModal(() =>
  import('./QuoteCreation/quoteCreation.modal').then((m) => ({
    default: m.QuoteCreationModal,
  })),
)

const InactivityWarningModal = createLazyModal(() =>
  import('./InactivityWarning/inactivityWarning.modal').then((m) => ({
    default: m.InactivityWarningModal,
  })),
)

const SelectOptionParentGroupModal = createLazyModal(() =>
  import('@/components/modals/SelectOptionParentGroup/selectOptionParentGroup.modal').then(
    (m) => ({
      default: m.SelectOptionParentGroupModal,
    }),
  ),
)

const ArchiveOptionReplacementModal = createLazyModal(() =>
  import('@/components/modals/ArchiveOptionReplacement/archiveOptionReplacement.modal').then(
    (m) => ({
      default: m.ArchiveOptionReplacementModal,
    }),
  ),
)

const OptionValidationRefusalModal = createLazyModal(() =>
  import('@/components/modals/OptionValidationRefusal/optionValidationRefusal.modal').then(
    (m) => ({
      default: m.OptionValidationRefusalModal,
    }),
  ),
)

export const modalRegistry: ModalConfigMap = {
  productStatus: {
    style: 'form',
    render: (props) => <ProductStatusModal.Lazy {...props} />,
  },
  productRequest: {
    style: 'form',
    render: (props) => <ProductRequestModal.Lazy {...props} />,
  },
  catalogPreview: {
    style: 'wide',
    render: (props) => <CatalogPreview.Lazy {...props} />,
  },
  deleteProducts: {
    style: 'form',
    render: (props) => <DeleteProductsModal.Lazy {...props} />,
  },
  editPlace: {
    style: 'form',
    render: (props) => <EditPlaceModal.Lazy {...props} />,
  },
  duplicateProject: {
    style: 'form',
    render: (props) => <DuplicateProjectModal.Lazy {...props} />,
  },
  confirmation: {
    style: 'info',
    render: (props) => <ConfirmationCard.Lazy {...props} />,
  },
  editTeamMember: {
    style: 'form',
    render: (props) => <EditTeamMemberModal.Lazy {...props} />,
  },
  documentUpload: {
    style: 'form',
    render: (props) => <DocumentUploadModal.Lazy {...props} />,
  },
  serviceUpload: {
    style: 'form',
    render: (props) => <ServiceUploadModal.Lazy {...props} />,
  },
  customerUpload: {
    style: 'form',
    render: (props) => <CustomerUploadModal.Lazy {...props} />,
  },
  userSurvey: {
    style: 'form',
    render: (props) => <UserSurveyModal.Lazy {...props} />,
  },
  confirmationCode: {
    style: 'form',
    render: (props) => <ModalConfirmationCode.Lazy {...props} />,
  },
  forgotPassword: {
    style: 'form',
    render: (props) => <ForgotPasswordModal.Lazy {...props} />,
  },
  newPasswordRequired: {
    style: 'form',
    render: (props) => <NewPasswordRequiredModal.Lazy {...props} />,
  },
  updatePassword: {
    style: 'form',
    render: (props) => <UpdatePasswordFormModal.Lazy {...props} />,
  },
  deleteAccount: {
    style: 'info',
    render: (props) => <DeleteAccountConfirmation.Lazy {...props} />,
  },
  deleteList: {
    style: 'info',
    render: (props) => <DeleteListModal.Lazy {...props} />,
  },
  editProperty: {
    style: 'form',
    render: (props) => (
      <EditProperty.Lazy
        key={[
          props.propertyId,
          props.field?.id ?? 'new',
          props.field?.key ?? '',
        ].join(':')}
        {...props}
      />
    ),
  },
  editAlert: {
    style: 'form',
    render: (props) => <EditAlert.Lazy {...props} />,
  },
  productDetails: {
    style: 'form',
    render: (props) => <ProductDetailsModal.Lazy {...props} />,
  },
  restoreProduct: {
    style: 'form',
    render: (props) => <RestoreProductModal.Lazy {...props} />,
  },
  tasks: {
    style: 'info',
    render: (props) => <TasksModal.Lazy {...props} />,
  },
  editOrganization: {
    style: 'form',
    render: (props) => <EditOrganizationModal.Lazy {...props} />,
  },
  alertResults: {
    style: 'info',
    render: (props) => <AlertResults.Lazy {...props} />,
  },
  catalogExportOptions: {
    style: 'form',
    render: (props) => <CatalogExportOptionsModal.Lazy {...props} />,
  },
  productEdit: {
    style: 'form',
    render: (props) => <ProductEditModal.Lazy {...props} />,
  },
  editMember: {
    style: 'form',
    render: ({
      teams,
      user,
      member,
      organizationMasterID,
      projectID,
      hideModal,
    }) => (
      <EditMember.Lazy
        teams={teams}
        user={user}
        member={member}
        organizationMasterID={organizationMasterID}
        projectID={projectID}
        hideModal={hideModal}
      />
    ),
  },
  editReference: {
    style: 'form',
    render: (props) => <EditReference.Lazy {...props} />,
  },
  optionsImport: {
    style: 'form',
    render: (props) => <OptionsImport.Lazy {...props} />,
  },
  qrCodeViewer: {
    style: 'wide',
    render: ({ dataUrls }) => (
      <PDFViewer showToolbar className="mt-14 h-[80vh]">
        <QRCodeDocument dataUrls={dataUrls} />
      </PDFViewer>
    ),
  },
  imageViewer: {
    style: 'info',
    render: ({ src, alt, width = 600, height = 500 }) => (
      <div className="flex items-center justify-center">
        <Image
          src={src}
          alt={alt}
          height={height}
          width={width}
          className="object-contain"
        />
      </div>
    ),
  },
  addSection: {
    style: 'form',
    render: (props) => <AddSectionModal.Lazy {...props} />,
  },
  editSection: {
    style: 'form',
    render: (props) => <EditSectionModal.Lazy {...props} />,
  },
  moveSection: {
    style: 'form',
    render: (props) => <MoveSectionModal.Lazy {...props} />,
  },
  editMarker: {
    style: 'form',
    render: (props) => <EditMarkerModal.Lazy {...props} />,
  },
  editZone: {
    style: 'form',
    render: (props) => <EditZoneModal.Lazy {...props} />,
  },
  importQuantity: {
    style: 'form',
    render: (props) => <ImportQuantityModal.Lazy {...props} />,
  },
  blueprintConfirmation: {
    style: 'info',
    render: (props) => <BlueprintConfirmationModal.Lazy {...props} />,
  },
  quoteCreation: {
    style: 'info',
    render: (props) => <QuoteCreationModal.Lazy {...props} />,
  },
  inactivityWarning: {
    style: 'info',
    render: (props) => <InactivityWarningModal.Lazy {...props} />,
  },
  selectOptionParentGroup: {
    style: 'form',
    render: (props) => <SelectOptionParentGroupModal.Lazy {...props} />,
  },
  archiveOptionReplacement: {
    style: 'form',
    render: (props) => <ArchiveOptionReplacementModal.Lazy {...props} />,
  },
  optionValidationRefusal: {
    style: 'form',
    render: (props) => <OptionValidationRefusalModal.Lazy {...props} />,
  },
}
